import Phaser from "phaser";
import { PALETTE, PALETTE_HEX } from "@/palette";
import type { PatientState } from "@/state/GameState";
import { resolveCharacterTexture } from "@/sprites/artManifest";

/**
 * Placeholder-era character (RFC §8): a chunky capsule person, engine-drawn
 * but BAKED to textures once per scene (generateTexture per the Phaser-4
 * graphics skill) so walkers are cheap sprites, not live Graphics objects.
 * Real sprite sheets replace the textures in v0.7; the behavior API stays.
 */

const BODY_W = 110;
const BODY_H = 150;
const HEAD_R = 42;
/** Full texture frame (padding for the outline stroke). */
const TEX_W = 130;
const TEX_H = 260;

const STATE_FILL: Record<PatientState, number> = {
  new: PALETTE.gray,
  progressing: PALETTE.green,
  issue: PALETTE.coralRed,
  paused: PALETTE.mustard,
};

/** Bake one capsule-person texture per fill color (idempotent per scene). */
export function bakeCharacterTexture(scene: Phaser.Scene, key: string, fill: number): void {
  if (scene.textures.exists(key)) return;
  const gfx = scene.make.graphics({ x: 0, y: 0 }, false);
  const cx = TEX_W / 2;
  // torso capsule
  gfx.fillStyle(fill);
  gfx.lineStyle(8, PALETTE.charcoal);
  gfx.fillRoundedRect(cx - BODY_W / 2, TEX_H - BODY_H - 8, BODY_W, BODY_H, 48);
  gfx.strokeRoundedRect(cx - BODY_W / 2, TEX_H - BODY_H - 8, BODY_W, BODY_H, 48);
  // head
  gfx.fillStyle(PALETTE.skin);
  gfx.fillCircle(cx, TEX_H - BODY_H - HEAD_R - 14, HEAD_R);
  gfx.strokeCircle(cx, TEX_H - BODY_H - HEAD_R - 14, HEAD_R);
  gfx.generateTexture(key, TEX_W, TEX_H);
  gfx.destroy();
}

export function bakeAllCharacterTextures(scene: Phaser.Scene): void {
  for (const [state, fill] of Object.entries(STATE_FILL)) {
    bakeCharacterTexture(scene, `person-${state}`, fill);
  }
  bakeCharacterTexture(scene, "person-staff", PALETTE.teal);
}

/**
 * A character on the floor: baked-texture sprite + reaction text, feet at
 * (x, y). Walks via tween chains; state swaps the texture.
 */
export class Character extends Phaser.GameObjects.Container {
  private sprite: Phaser.GameObjects.Image;
  private reaction: Phaser.GameObjects.Text;
  private reactionTween: Phaser.Tweens.Tween | undefined;

  constructor(scene: Phaser.Scene, x: number, y: number, textureKey: string) {
    super(scene, x, y);
    // Real pack if one loaded for this key (runtime art layer), else the baked
    // placeholder — resolveCharacterTexture is the whole fallback.
    this.sprite = scene.add.image(0, 0, resolveCharacterTexture(textureKey)).setOrigin(0.5, 1);
    this.reaction = scene.add
      .text(40, -this.sprite.height - 6, "", {
        fontFamily: "Georgia, serif",
        fontSize: "40px",
        fontStyle: "bold",
        color: PALETTE_HEX.charcoal,
      })
      .setOrigin(0, 1);
    this.add([this.sprite, this.reaction]);
    this.setDepth(y);
    scene.add.existing(this);
  }

  setPatientState(state: PatientState): void {
    this.sprite.setTexture(resolveCharacterTexture(`person-${state}`));
    this.reaction.setY(-this.sprite.height - 6);
    this.clearReaction();
    switch (state) {
      case "progressing":
        this.setReaction("z Z z", PALETTE_HEX.teal, { alpha: 0.25, yoyo: true, repeat: -1, duration: 800 });
        break;
      case "issue":
        this.setReaction("💧!", PALETTE_HEX.coralRed, { x: "+=6", yoyo: true, repeat: -1, duration: 70 });
        break;
      case "paused":
        this.setReaction("✶ ✶", PALETTE_HEX.gold, { angle: 360, repeat: -1, duration: 2400 });
        break;
      case "new":
        break;
    }
  }

  private setReaction(text: string, color: string, tween: Record<string, unknown>): void {
    this.reaction.setText(text).setColor(color);
    this.reactionTween = this.scene.tweens.add({ targets: this.reaction, ...tween });
  }

  private clearReaction(): void {
    this.reactionTween?.remove();
    this.reactionTween = undefined;
    this.reaction.setText("").setAngle(0).setAlpha(1).setX(40);
  }

  /** Walk to (x, y) with a slight bob, then call back. */
  walkTo(x: number, y: number, onDone?: () => void): void {
    // A new order supersedes any walk in flight — competing chains on the
    // same body fight each other (v0.2 UI-leg finding M3).
    this.scene.tweens.killTweensOf(this);
    const dist = Math.abs(x - this.x) + Math.abs(y - this.y);
    this.scene.tweens.chain({
      targets: this,
      tweens: [{ x, y, duration: Math.max(300, dist * 1.1), ease: "Sine.easeInOut" }],
      onComplete: () => {
        this.setDepth(this.y);
        onDone?.();
      },
    });
    // bob while moving (separate short tween loop, cleaned by duration)
    this.scene.tweens.add({
      targets: this.sprite,
      y: -8,
      duration: 140,
      yoyo: true,
      repeat: Math.max(1, Math.floor(dist / 90)),
    });
  }

  /** Graduation: confetti pop + bounce, then walk off right and destroy. */
  celebrateAndExit(exitX: number, onDone?: () => void): void {
    this.clearReaction();
    const colors = [PALETTE.teal, PALETTE.mustard, PALETTE.coralRed, PALETTE.green, PALETTE.gold];
    for (let i = 0; i < 16; i++) {
      const c = this.scene.add
        .rectangle(this.x, this.y - TEX_H, 16, 16, colors[i % colors.length]!)
        .setDepth(this.depth + 1);
      this.scene.tweens.add({
        targets: c,
        x: this.x + Phaser.Math.Between(-160, 160),
        y: this.y - TEX_H + Phaser.Math.Between(-140, 90),
        angle: Phaser.Math.Between(-180, 180),
        alpha: 0,
        duration: 750,
        ease: "Cubic.easeOut",
        onComplete: () => c.destroy(),
      });
    }
    this.scene.tweens.chain({
      targets: this,
      tweens: [
        { y: this.y - 50, duration: 140, yoyo: true, repeat: 1, ease: "Quad.easeOut" },
        { x: exitX, duration: 900, ease: "Sine.easeIn" },
      ],
      onComplete: () => {
        this.destroy();
        onDone?.();
      },
    });
  }

  /** Balk (M2): startled hop, dust puff, dash back out the way they came. */
  balkAndLeave(exitX: number, onDone?: () => void): void {
    this.reaction.setText("!").setColor(PALETTE_HEX.coralRed);
    const puff = this.scene.add
      .ellipse(this.x, this.y - 20, 90, 45, PALETTE.gray, 0.6)
      .setDepth(this.depth - 1);
    this.scene.tweens.add({ targets: puff, alpha: 0, scaleX: 2, duration: 500, onComplete: () => puff.destroy() });
    this.scene.tweens.chain({
      targets: this,
      tweens: [
        { y: this.y - 36, duration: 110, yoyo: true, ease: "Quad.easeOut" },
        { x: exitX, duration: 450, ease: "Sine.easeIn" },
      ],
      onComplete: () => {
        this.destroy();
        onDone?.();
      },
    });
  }

  /** Death: slow wilt; the scene places the flower. */
  wilt(onDone?: () => void): void {
    this.clearReaction();
    this.scene.tweens.add({
      targets: this,
      scaleY: 0.15,
      alpha: 0,
      duration: 900,
      ease: "Quad.easeIn",
      onComplete: () => {
        this.destroy();
        onDone?.();
      },
    });
  }
}
